// MainStem - UI
import { theme } from '@mainstem/react-mainstem'
// Icons
import {
  faBook,
  faBrowser,
  faChartLine,
  faFileLines,
  faShippingFast,
  faBoxes,
  faCalendar,
  faCogs,
  faFileSearch,
  faInventory,
  faLink,
  faSitemap,
  faFileInvoiceDollar,
  faBoxesPacking
} from '@fortawesome/pro-light-svg-icons'
// MainStem - API
import { MainStemAPIPlatformControllersPurchasingAuthCurrentUserAPIResponse } from 'api-new'
// Global - Helpers
import { permissionCheck } from 'helpers'

export const links = async (loggedInUser: MainStemAPIPlatformControllersPurchasingAuthCurrentUserAPIResponse) => {
  const hasIntegrationSettingsAccess = permissionCheck(loggedInUser, 'Settings', 'ViewIntegrations', 'true')

  const hasPunchOutSettingsAccess = permissionCheck(loggedInUser, 'Settings', 'ViewPunchouts', 'true')

  const canShopPunchOuts = permissionCheck(loggedInUser, 'Marketplace', 'AllowPunchOuts', 'true')

  const hasAccountSettingsAccess = permissionCheck(loggedInUser, 'Settings', 'ViewAccountSettings', 'true')

  const hasDirectPunchOut = loggedInUser.organization?.allowPunchOutInMainNav

  const canUpdateFinancialDimensions = permissionCheck(loggedInUser, 'Settings', 'EditFinancialDimensions', 'true')

  const hasDashboardReceiving = permissionCheck(loggedInUser, 'Dashboard', 'DashboardReceiving', 'true')

  const accessBulkOrdering = permissionCheck(loggedInUser, 'Settings', 'AccessBulkOrdering', 'true')

  const accessRFQ = permissionCheck(loggedInUser, 'Order', 'RFQ', 'true')

  const hasAnySettingsAccess =
    hasIntegrationSettingsAccess ||
    hasPunchOutSettingsAccess ||
    hasAccountSettingsAccess ||
    canUpdateFinancialDimensions

  const canShowPurchasingPunchOuts = canShopPunchOuts && hasDirectPunchOut
  const canShowSettingsPunchOuts = hasDirectPunchOut && hasPunchOutSettingsAccess

  const navigationLinks = [
    {
      title: 'Home',
      to: '/dashboard/marketplace',
      icon: faChartLine,
      bases: ['/dashboard/']
    },
    {
      icon: faSitemap,
      title: 'Organization',
      bases: ['/catalogs', '/departments', '/locations', '/organization', '/users'],
      links: [
        {
          title: 'Locations',
          to: '/locations/dashboard',
          icon: theme.icons.mainstem.locations,
          bases: ['/locations']
        },
        {
          title: 'Departments',
          to: '/departments/dashboard',
          icon: theme.icons.mainstem.departments,
          bases: ['/departments']
        },
        {
          title: 'Users',
          to: '/users/dashboard',
          icon: theme.icons.mainstem.users,
          bases: ['/users']
        },
        {
          title: 'Catalogs',
          to: '/catalogs/dashboard',
          icon: theme.icons.mainstem.catalogs,
          bases: ['/catalogs']
        }
      ]
    },
    {
      icon: faLink,
      title: 'Supply Chain',
      bases: ['/suppliers', '/products'],
      links: [
        {
          title: 'Suppliers',
          to: '/suppliers/dashboard',
          icon: theme.icons.mainstem.suppliers,
          bases: ['/suppliers']
        },
        {
          title: 'Products',
          to: '/products/dashboard',
          icon: theme.icons.mainstem.products,
          bases: ['/products']
        }
      ]
    },
    {
      icon: faBoxes,
      title: 'Purchasing',
      bases: ['/orders', '/requisitions', '/punch-outs/purchase', '/invoices/dashboard'],
      links: [
        {
          title: 'Requisitions',
          to: '/requisitions/dashboard',
          icon: theme.icons.mainstem.requisition,
          bases: ['/requisitions']
        },
        accessBulkOrdering && {
          title: 'Bulk Ordering',
          to: '/requisitions/bulk-ordering/dashboard',
          icon: faFileLines,
          bases: ['/requisitions/bulk-ordering']
        },
        {
          title: 'Purchase Orders',
          to: '/purchase-orders/dashboard',
          icon: theme.icons.mainstem.orders,
          bases: ['/purchase-orders/dashboard']
        },
        {
          title: 'Invoices',
          to: '/invoices/dashboard',
          icon: theme.icons.mainstem.invoices,
          bases: ['/invoices/dashboard']
        },
        accessRFQ && {
          title: 'Request For Quote',
          to: '/rfq/dashboard',
          icon: theme.icons.mainstem.rfq,
          bases: ['/rfq']
        },
        canShowPurchasingPunchOuts && {
          title: 'Punch-Outs',
          to: '/punch-outs/purchase',
          icon: faBrowser,
          bases: ['/punch-outs']
        }
      ].filter(Boolean)
    },
    {
      icon: faInventory,
      title: 'Logistics',
      bases: ['/shipments', '/receiving'],
      links: [
        {
          title: 'Shipments',
          to: '/shipments/dashboard',
          icon: theme.icons.mainstem.shipments,
          bases: ['/shipments']
        },
        hasDashboardReceiving && {
          title: 'Receiving',
          to: '/receiving/dashboard',
          icon: theme.icons.mainstem.receiving,
          bases: ['/receiving']
        }
      ].filter(Boolean)
    },
    {
      icon: faFileSearch,
      title: 'Reporting',
      bases: ['/reports'],
      links: [
        {
          title: 'Line Items',
          to: '/reports/line-items',
          icon: faFileLines,
          bases: ['/reports/line-items']
        },
        {
          title: 'Unfulfilled Requisitions',
          to: '/reports/unfulfilled-requisitions',
          icon: faShippingFast,
          bases: ['/reports/unfulfilled-requisitions']
        },
        {
          title: 'Order History',
          to: '/reports/order-history',
          icon: faCalendar,
          bases: ['/reports/order-history']
        },
        {
          title: 'Accruals Report',
          to: '/reports/accruals',
          icon: faFileInvoiceDollar,
          bases: ['/reports/accruals']
        },
        {
          title: 'Punch Outs',
          to: '/reports/punch-outs',
          icon: faBrowser,
          bases: ['/reports/punch-outs']
        }
      ]
    },
    hasAnySettingsAccess && {
      icon: faCogs,
      title: 'Settings',
      bases: ['/integrations', '/punch-outs/settings'],
      links: [
        (hasAccountSettingsAccess || canUpdateFinancialDimensions) && {
          title: 'Account',
          to: '/account',
          icon: faBook,
          bases: ['/account']
        },
        hasIntegrationSettingsAccess && {
          title: 'Integrations',
          to: '/integrations/dashboard',
          icon: theme.icons.mainstem.integrations,
          bases: ['/integrations']
        },
        canShowSettingsPunchOuts && {
          title: 'Punch-Outs',
          to: '/punch-outs/settings',
          icon: faBrowser,
          bases: ['/punch-outs']
        }
      ].filter(Boolean)
    }
  ].filter(Boolean)

  return navigationLinks
}
